import type {
	BlockchainTicker,
	Customer,
	CustomerPagination,
	ListCustomersParams,
	ListOrdersParams,
	ListPaymentParams,
	Merchant,
	MerchantBase,
	MerchantNotification,
	MerchantNotificationUpsertRequest,
	NotificationTestResult,
	Order,
	OrderParams,
	OrdersPagination,
	Payment,
	PaymentParams,
	PaymentsPagination,
	SupportMessage,
	WebhookSettings,
} from "src/types";
import apiRequest from "src/utils/api-request";
import withApiPath from "src/utils/with-api-path";

const merchantProvider = {
	async storeMerchant(params: MerchantBase): Promise<Merchant> {
		const response = await apiRequest.post(withApiPath("/merchant"), params);
		return response.data;
	},

	async listMerchants(): Promise<Merchant[]> {
		const response = await apiRequest.get(withApiPath("/merchant"));
		return response.data?.results;
	},

	async showMerchant(merchantId: string): Promise<Merchant> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}`),
		);
		return response.data;
	},

	async deleteMerchant(merchantId: string): Promise<Merchant> {
		const response = await apiRequest.delete(
			withApiPath(`/merchant/${merchantId}`),
		);
		return response.data;
	},

	async updateMerchant(
		merchantId: string,
		params: MerchantBase,
	): Promise<Merchant> {
		const response = await apiRequest.put(
			withApiPath(`/merchant/${merchantId}`),
			params,
		);
		return response.data;
	},

	async updateMerchantWebhookSettings(
		merchantId: string,
		params: WebhookSettings,
	): Promise<void> {
		await apiRequest.put(
			withApiPath(`/merchant/${merchantId}/webhook`),
			params,
		);
		return;
	},

	async listPayments(
		merchantId: string,
		params?: ListPaymentParams,
	): Promise<PaymentsPagination> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/payment`),
			{ params },
		);
		return response.data;
	},

	async getPayment(merchantId: string, paymentId: string): Promise<Payment> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/payment/${paymentId}`),
		);
		return response.data;
	},

	async createPayment(
		merchantId: string,
		params: PaymentParams,
	): Promise<Payment> {
		const response = await apiRequest.post(
			withApiPath(`/merchant/${merchantId}/payment`),
			params,
		);
		return response.data;
	},

	async listOrders(
		merchantId: string,
		params?: ListOrdersParams,
	): Promise<OrdersPagination> {
		// Clean up undefined/empty params
		const cleanParams = params
			? Object.fromEntries(
					Object.entries(params).filter(([, v]) => v !== undefined && v !== ""),
				)
			: undefined;
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/order`),
			{ params: cleanParams },
		);
		return response.data;
	},

	async getOrder(merchantId: string, orderId: string): Promise<Order> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/order/${orderId}`),
		);
		return response.data;
	},

	async createOrder(merchantId: string, params: OrderParams): Promise<Order> {
		const response = await apiRequest.post(
			withApiPath(`/merchant/${merchantId}/order`),
			params,
		);
		return response.data;
	},

	async triggerOrderWebhook(
		merchantId: string,
		orderId: string,
	): Promise<{ status: string; orderId: string }> {
		const response = await apiRequest.post(
			withApiPath(`/merchant/${merchantId}/order/${orderId}/webhook`),
		);
		return response.data;
	},

	async triggerPaymentWebhook(
		merchantId: string,
		paymentId: string,
	): Promise<{ status: string; paymentId: string }> {
		const response = await apiRequest.post(
			withApiPath(`/merchant/${merchantId}/payment/${paymentId}/webhook`),
		);
		return response.data;
	},

	async updateSupportedMethods(
		merchantId: string,
		params: { supportedPaymentMethods: BlockchainTicker[] },
	): Promise<void> {
		const response = await apiRequest.put(
			withApiPath(`/merchant/${merchantId}/supported-method`),
			params,
		);
		return response.data;
	},

	async sendSupportMessage(
		merchantId: string,
		params: SupportMessage,
	): Promise<void> {
		await apiRequest.post(withApiPath(`/merchant/${merchantId}/form`), params);
		return;
	},

	async listCustomers(
		merchantId: string,
		params?: ListCustomersParams,
	): Promise<CustomerPagination> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/customer`),
			{ params },
		);
		return response.data;
	},

	async getCustomerDetails(
		merchantId: string,
		customerId: string,
	): Promise<Customer> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/customer/${customerId}`),
		);
		return response.data;
	},

	async getMerchantLimits(): Promise<{
		currentCount: number;
		maxAllowed: number;
		isUnlimited: boolean;
	}> {
		const response = await apiRequest.get(withApiPath("/merchant/limits"));
		return response.data;
	},

	async listMerchantNotifications(
		merchantId: string,
	): Promise<MerchantNotification[]> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/notification`),
		);
		return response.data?.results ?? [];
	},

	async getMerchantNotification(
		merchantId: string,
		channel: string,
	): Promise<MerchantNotification> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/notification/${channel}`),
		);
		return response.data;
	},

	async upsertMerchantNotification(
		merchantId: string,
		channel: string,
		params: MerchantNotificationUpsertRequest,
	): Promise<MerchantNotification> {
		const response = await apiRequest.put(
			withApiPath(`/merchant/${merchantId}/notification/${channel}`),
			params,
		);
		return response.data;
	},

	async testMerchantNotification(
		merchantId: string,
		channel: string,
	): Promise<NotificationTestResult> {
		const response = await apiRequest.post(
			withApiPath(`/merchant/${merchantId}/notification/${channel}/test`),
		);
		return response.data;
	},

	async deleteMerchantNotification(
		merchantId: string,
		channel: string,
	): Promise<void> {
		await apiRequest.delete(
			withApiPath(`/merchant/${merchantId}/notification/${channel}`),
		);
	},
};

export default merchantProvider;
